import { useState, type FormEvent } from 'react';
import { api, ApiError } from '../api/client.ts';
import { t } from '../i18n/index.ts';
import { formatRemaining } from '../lib/format.ts';

export function Login({ onSignedIn }: { onSignedIn: () => void }): React.ReactElement {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.login(email, password);
      onSignedIn();
    } catch (err) {
      setError(describe(err));
      setBusy(false);
    }
  }

  return (
    <div className="login-page">
      <form className="login-card" onSubmit={submit}>
        <span className="eyebrow">ASHAMS</span>
        <h1>{t('auth.login')}</h1>
        <p className="sub">{t('app.title')}</p>

        {error !== null && <div className="form-error" role="alert">{error}</div>}

        <div className="field">
          <label htmlFor="email">{t('auth.email')}</label>
          {/* The address itself is Latin text inside an RTL page. */}
          <input
            id="email"
            type="email"
            dir="ltr"
            autoComplete="username"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>

        <div className="field">
          <label htmlFor="password">{t('auth.password')}</label>
          <input
            id="password"
            type="password"
            dir="ltr"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </div>

        <button type="submit" className="primary" disabled={busy} style={{ width: '100%' }}>
          {busy ? t('auth.signing_in') : t('auth.login')}
        </button>
      </form>
    </div>
  );
}

/**
 * Turns a failure into something a producer can act on. A lockout says when to
 * come back; everything else stays deliberately vague, because a more specific
 * message here would say whether the account exists.
 */
function describe(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.code === 'too_many_attempts') {
      const seconds = Number(err.details['retry_after_seconds'] ?? 0);
      return t('auth.locked', { time: formatRemaining(seconds * 1000) });
    }
    if (err.status === 0) return t('error.network');
    if (err.status === 401) return t('auth.invalid');
  }
  return t('error.unknown');
}
